/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { SpacingKey } from '@/styles/HDM/customSpacing';
import { ThemeOptions } from '@mui/material/styles';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

declare module '@mui/material/Alert' {
	interface AlertProps {
		alpha?: 'true' | 'false';
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		inline: true;
	}
	interface ButtonOwnProps {
		alpha?: boolean;
	}
}

declare module '@mui/material/Stack' {
	interface StackOwnProps {
		alpha?: boolean;
	}
}
declare module '@mui/material/TextField' {
	interface FilledTextFieldProps {
		alpha?: boolean;
	}
	interface StandardTextFieldProps {
		alpha?: boolean;
	}
	interface OutlinedTextFieldProps {
		alpha?: boolean;
	}
}

declare module '@mui/material/FormControlLabel' {
	interface FormControlLabelProps {
		alpha?: boolean;
	}
}

declare module '@mui/material/Checkbox' {
	interface CheckboxProps {
		alpha?: boolean;
	}
}

declare module '@mui/material/Divider' {
	interface DividerOwnProps {
		alpha?: boolean;
	}
}

declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		body1AsH1: true;
		body1AsH2: true;
		body2AsH2: true;
		h3AsH1: true;
		h4AsH1: true;
		h6AsH2: true;
		strong: true;
		spanacce: true; // for accessibility purposes, i.e. aria descriptions
		headingDisplay: true;
		headingMainTitle: true;
		headingHeadline: true;
		headingHeadlineLight: true;
		headingSubhead: true;
		headingTitle: true;
		headingSubtitle: true;
		headingSegment: true;
		headingFeatured: true;
		bodyBaseline: true;
		bodySubText: true;
		bodyCaption: true;
		pricesLarge: true;
		pricesMedium: true;
		pricesSmall: true;
		pricesExtraSmall: true;
		textSegment: true;
	}
	interface TypographyOwnProps {
		alpha?: boolean;
	}
}
declare module '@mui/material/styles' {
	interface TypeText {
		expandedMenu: string;
		alert: string;
		highlight: string;
	}
	interface TypographyVariants {
		headingDisplay?: React.CSSProperties;
		headingMainTitle?: React.CSSProperties;
		headingHeadline?: React.CSSProperties;
		headingSubhead?: React.CSSProperties;
		headingTitle?: React.CSSProperties;
		headingSubtitle?: React.CSSProperties;
		headingSegment?: React.CSSProperties;
		headingFeatured?: React.CSSProperties;
		bodyBaseline?: React.CSSProperties;
		bodySubText?: React.CSSProperties;
		bodyCaption?: React.CSSProperties;
		pricesLarge?: React.CSSProperties;
		pricesMedium?: React.CSSProperties;
		pricesSmall?: React.CSSProperties;
		pricesExtraSmall?: React.CSSProperties;
		textSegment?: React.CSSProperties;
	}
	interface TypographyVariantsOptions {
		headingDisplay?: React.CSSProperties;
		headingMainTitle?: React.CSSProperties;
		headingHeadline?: React.CSSProperties;
		headingSubhead?: React.CSSProperties;
		headingTitle?: React.CSSProperties;
		headingSubtitle?: React.CSSProperties;
		headingSegment?: React.CSSProperties;
		headingHeadlineLight?: React.CSSProperties;
		headingFeatured?: React.CSSProperties;
		bodyBaseline?: React.CSSProperties;
		bodySubText?: React.CSSProperties;
		bodyCaption?: React.CSSProperties;
		pricesLarge?: React.CSSProperties;
		pricesMedium?: React.CSSProperties;
		pricesSmall?: React.CSSProperties;
		pricesExtraSmall?: React.CSSProperties;
		textSegment?: React.CSSProperties;
	}

	interface Theme {
		customSpacing: (value: number | SpacingKey) => number;
		contrastText: {
			primary: {
				light: string;
				main: string;
				dark: string;
			};
			secondary: {
				main: string;
				light: string;
				dark: string;
			};
			grey: {
				'50': string;
				'100': string;
				'200': string;
				'300': string;
				'400': string;
				'500': string;
			};
			success: {
				light: string;
				main: string;
				dark: string;
			};
			warning: {
				light: string;
				main: string;
				dark: string;
			};
			error: {
				light: string;
				main: string;
				dark: string;
			};
		};
		elevationShadows: {
			stickyShadows: {
				default: number;
			};
			cardPopupShadows: {
				default: number;
			};
			paperShadows: {
				default: number;
			};
		};
	}

	interface ThemeOptions {
		customSpacing?: (value: number | SpacingKey) => number;
		elevationShadows?: {
			stickyShadows?: {
				default?: number;
			};
			cardPopupShadows?: {
				default?: number;
			};
			paperShadows?: {
				default?: number;
			};
		};
		contrastText?: {
			primary?: {
				light?: string;
				main?: string;
				dark?: string;
			};
			secondary?: {
				main?: string;
				light?: string;
				dark?: string;
			};
			grey?: {
				'50'?: string;
				'100'?: string;
				'200'?: string;
				'300'?: string;
				'400'?: string;
				'500'?: string;
			};
			success?: {
				light?: string;
				main?: string;
				dark?: string;
			};
			warning?: {
				light?: string;
				main?: string;
				dark?: string;
			};
			error?: {
				light?: string;
				main?: string;
				dark?: string;
			};
		};
		breakpoints?: {
			values: {
				xs: number;
				sm: number;
				md: number;
				lg: number;
				xl: number;
				custom?: number;
				tablet?: number;
			};
		};
	}
}

declare module '@mui/material/IconButton' {
	interface IconButtonPropsVariantOverrides {
		navigation: true;
	}

	interface IconButtonOwnProps {
		type?: 'navigation' | 'button' | 'reset' | 'submit';
		shape?: 'default' | 'round' | 'sharp';
		alpha?: boolean;
	}
}

export const typography: ThemeOptions = {
	typography: {
		htmlFontSize: 16,
		fontWeightRegular: 400,
		fontFamily: ['Open Sans', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
		h1: {
			fontSize: 72,
			fontWeight: OpenSansFontWeights.bold,
			lineHeight: 1.2,
		},
		h2: {
			fontSize: 52,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h3: {
			fontSize: 32,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h4: {
			fontSize: 26,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h5: {
			fontSize: 20,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h6: {
			fontSize: 18,
			fontWeight: 500,
			lineHeight: 1.2,
		},
		subtitle1: {
			fontSize: 18,
			fontWeight: 500,
			lineHeight: 1.2,
		},
		textSegment: {
			fontSize: 18,
			fontWeight: OpenSansFontWeights.light,
			lineHeight: 1.2,
		},
		subtitle2: {
			fontSize: 18,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		body1: {
			fontSize: 15,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		body2: {
			fontSize: 14,
			fontWeight: 500,
			lineHeight: 1.2,
		},
		button: {
			fontSize: 15,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
			textTransform: 'none',
		},
		caption: {
			fontSize: 12,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		strong: {
			fontWeight: OpenSansFontWeights.semibold,
			lineHeight: 1.2,
		},
		overline: {
			fontSize: 12,
			textTransform: 'uppercase',
			fontWeight: OpenSansFontWeights.semibold,
			lineHeight: 1.2,
			letterSpacing: 1,
		},
		body1AsH1: {
			fontSize: 14,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		body1AsH2: {
			fontSize: 14,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		body2AsH2: {
			fontSize: 14,
			fontWeight: 500,
			lineHeight: 1.2,
		},
		h3AsH1: {
			fontSize: 32,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h4AsH1: {
			fontSize: 26,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		h6AsH2: {
			fontSize: 18,
			fontWeight: 500,
			lineHeight: 1.2,
		},
		headingDisplay: {
			fontSize: 44,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingMainTitle: {
			fontSize: 36,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingHeadline: {
			fontSize: 30,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingHeadlineLight: {
			fontSize: 30,
			fontWeight: OpenSansFontWeights.light,
			lineHeight: 1.2,
		},
		headingSubhead: {
			fontSize: 27,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingTitle: {
			fontSize: 25,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingSubtitle: {
			fontSize: 20,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingSegment: {
			fontSize: 18,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		headingFeatured: {
			fontSize: 18,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		bodyBaseline: {
			fontSize: 15,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		bodySubText: {
			fontSize: 12,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		bodyCaption: {
			fontSize: 10,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		pricesLarge: {
			fontSize: 36,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		pricesMedium: {
			fontSize: 27,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		pricesSmall: {
			fontSize: 21,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
		pricesExtraSmall: {
			fontSize: 16,
			fontWeight: OpenSansFontWeights.regular,
			lineHeight: 1.2,
		},
	},
};
